import { Column } from '@app/components/data-grid/data-grid';
import { useAnalytics } from '@app/features/analytics/hooks/use-analytics';
import { useSpaceContext } from '@app/features/analytics/hooks/use-space-context';
import { selectOrganizationId } from '@app/store/selectors';
import { useAppDispatch, useAppSelector } from '@app/store/store';
import { useTranslation } from '@hopin-team/provider-translation';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { DataGrid } from '../../components';
import { ErrorScreen } from '../../components/error-states/error-screen';
import { NoFilterResults } from '../../components/error-states/no-filter-results';
import { useFetch } from '../../hooks/use-fetch';
import { selectAnalyticsSeriesInfo } from '../../selectors';
import { fetchAggregatedEvents, fetchEvents } from '../../services/endpoints';
import { buildQuery, formatDate, minutesToReadableTime } from '../../utils';
import { EventTypeTemplate } from '../events/event-type-template';
import { LoaderSkeleton } from '../events/loader-skeleton';
import { EventType } from '../events/types';
import { EngagementScoreTemplate } from '../registrants/engagement-score-template';
import { EventLoaderSkeleton } from './event-loader-skeleton';
import { EventNameTemplate } from './event-name-template';
import { NoResults } from './no-results';
import { SeriesNameTemplate } from './series-name';

export interface AggregatedSeriesType {
  id?: string | number;
  eventSeriesId: string;
  name: string;
  eventType: string;
  startTime: string;
  avgEngagementScore: number;
  avgMinutes: number;
  totalRegistrants: number;
  totalAttendees: number;
  avgTurnout: number;
  externalId: string;
  eventsCount?: number;
  seriesImage?: Array<{ url?: string } | null>;
  timeStart?: string;
  timeEnd?: string;
}

export const SeriesColumns = (): Column<AggregatedSeriesType>[] => {
  const { t } = useTranslation();
  return [
    {
      text: t('analytics.name'),
      name: 'name',
      template: SeriesNameTemplate,
      width: '300px',
    },
    {
      text: t('analytics.date'),
      name: 'timeStart',
      width: '220px',
      formatter: (value: string, rowData?: AggregatedSeriesType) => {
        return (
          formatDate(value || '') + ' -> ' + formatDate(rowData?.timeEnd || '')
        );
      },
      sort: false,
    },
    {
      text: t('analytics.event-type'),
      name: 'eventType',
      template: EventTypeTemplate,
      sort: false,
    },
    {
      text: t('analytics.avg-engagement-score'),
      name: 'avgEngagementScore',
      sort: false,
      template: EngagementScoreTemplate,
    },
    {
      text: t('analytics.avg-time-spent'),
      name: 'avgMinutes',
      sort: false,
      formatter: value => {
        return !!value && value !== 'null'
          ? minutesToReadableTime(Number(value))
          : '';
      },
    },
    {
      text: t('analytics.registrants'),
      name: 'totalRegistrants',
      sort: false,
      formatter: value => {
        return !!value && value !== 'null'
          ? `${Number(value).toFixed(0)}`
          : t('analytics.not-available');
      },
    },
    {
      text: t('analytics.attendees'),
      name: 'totalAttendees',
      sort: false,
      formatter: value => {
        return !!value && value !== 'null'
          ? `${Number(value).toFixed(0)}`
          : t('analytics.not-available');
      },
    },
    {
      text: t('analytics.turnout'),
      name: 'avgTurnout',
      sort: false,
      formatter: value => {
        return !!value && value !== 'null'
          ? `${Number(value).toFixed(2)}%`
          : t('analytics.not-available');
      },
    },
  ];
};

const CHILD_GRID_PAGE_SIZE = 10;

export const Series = () => {
  const { seriesData, isLoading, error, filters, sortConfig } = useSelector(
    selectAnalyticsSeriesInfo,
  );
  const orgId = useAppSelector(selectOrganizationId) || '';
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { spaceExternalId } = useSpaceContext();

  const {
    data: aggregatedEvents,
    isLoading: aggregatedEventsLoading,
    isError: aggregatedEventsError,
  } = useFetch({ spaceExternalId }, `aggregatedSeries-${orgId}`, () =>
    fetchAggregatedEvents(spaceExternalId || undefined),
  );

  const aggregatedSeriesData = aggregatedEvents?.eventSeries.map(
    (series: AggregatedSeriesType) => ({
      ...series,
      eventType: 'Series',
      id: series.externalId || series.eventSeriesId,
    }),
  );

  const { track } = useAnalytics();

  const handleSort = (columnKey: string, direction: 'asc' | 'desc' | '') => {
    dispatch({
      type: '[analytics] update series sort key and direction',
      payload: {
        name: columnKey,
        reverseSort: direction === 'asc',
      },
    });
    track('ola_series_tab_sorting_click', {
      columnKey: columnKey,
      direction: direction,
    });
  };

  // Memoized fetch callback for child data - used by DataGrid internally
  const fetchChildEventsCallback = useCallback(fetchEvents, []);

  // Fetch function for initial child rows - used by DataGrid's ChildRowRenderer
  const fetchChildRows = useCallback(
    async (parentRow: AggregatedSeriesType): Promise<EventType[]> => {
      const externalId = parentRow.externalId;

      const childConfig = {
        eventSeriesId: externalId,
        page: '1',
        perPage: CHILD_GRID_PAGE_SIZE.toString(),
        spaceExternalId,
      };

      const childData = await fetchChildEventsCallback(buildQuery(childConfig));
      return childData.events || [];
    },
    [fetchChildEventsCallback, spaceExternalId],
  );

  // Fetch function for paginated child rows - used by DataGrid's ChildRowRenderer
  const fetchMoreChildRows = useCallback(
    async (
      parentRow: AggregatedSeriesType,
      currentPage: number,
    ): Promise<EventType[]> => {
      const externalId = parentRow.externalId;

      const childConfig = {
        eventSeriesId: externalId,
        page: currentPage.toString(),
        perPage: CHILD_GRID_PAGE_SIZE.toString(),
        spaceExternalId,
      };

      const childData = await fetchChildEventsCallback(buildQuery(childConfig));
      return childData.events || [];
    },
    [fetchChildEventsCallback, spaceExternalId],
  );

  const childColumns = useCallback((): Column<EventType>[] => {
    return [
      {
        text: t('analytics.event'),
        name: 'name',
        template: EventNameTemplate,
        width: '300px',
      },
      {
        text: t('analytics.date'),
        name: 'startTime',
        formatter: (value: string, rowData: any) => {
          return formatDate(value) + ' -> ' + formatDate(rowData.endTime);
        },
      },
      {
        text: t('analytics.event-type'),
        name: 'venueType',
        template: EventTypeTemplate,
      },
      {
        text: t('analytics.engagementScore'),
        name: 'engagementScore',
        template: EngagementScoreTemplate,
      },
      {
        text: t('analytics.averageMinutes'),
        name: 'averageMinutes',
        formatter: value => {
          return !!value && value !== 'null'
            ? minutesToReadableTime(Number(value))
            : '';
        },
      },
      { text: t('analytics.registrantsNumber'), name: 'registrantsNumber' },
      { text: t('analytics.attendeesNumber'), name: 'attendeesNumber' },
      {
        text: t('analytics.turnout'),
        name: 'turnout',
        formatter: value => {
          return !!value && value !== 'null'
            ? `${Number(value).toFixed(2)}%`
            : t('analytics.not-available');
        },
      },
    ];
  }, [t]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const transformedSeriesData = useMemo(() => {
    if (!aggregatedSeriesData || !seriesData.data) {
      return [];
    }

    const dictionary = new Map<string | number, AggregatedSeriesType>();
    aggregatedSeriesData.forEach((series: AggregatedSeriesType) => {
      dictionary.set(series.id || '', series);
    });

    const result = (seriesData.data as unknown as AggregatedSeriesType[])
      .filter((series: AggregatedSeriesType) => dictionary.has(series.id || ''))
      .filter((series: AggregatedSeriesType) => (series.eventsCount ?? 0) > 0)
      .map((series: AggregatedSeriesType) => ({
        ...series,
        ...dictionary.get(series.id || ''),
      }));
    return result;
  }, [aggregatedSeriesData, seriesData]);

  const fetchMore = () => {
    if (
      !isLoading &&
      seriesData.pagination?.hasNextPage &&
      seriesData.pagination?.endCursor
    ) {
      dispatch({
        type: '[analytics] series fetch next page',
        payload: {
          cursor: seriesData.pagination.endCursor,
        },
      });
    }
  };

  const getChildLoaderCount = (
    rowData: AggregatedSeriesType,
    currentPage: number,
  ) => {
    const totalEvents = rowData.eventsCount || 0;
    const remainingEvents =
      totalEvents - (currentPage - 1) * CHILD_GRID_PAGE_SIZE;
    return remainingEvents < 10 ? remainingEvents : CHILD_GRID_PAGE_SIZE;
  };

  const handleExpandCollapse = (
    row: AggregatedSeriesType,
    isExpanded: boolean,
  ) => {
    if (isExpanded) {
      track('ola_seriestab_series_collapse', {
        seriesId: row.id,
      });
    } else {
      track('ola_seriestab_series_expand', {
        seriesId: row.id,
      });
    }
  };

  return (
    <DataGrid<AggregatedSeriesType, EventType>
      columns={SeriesColumns()}
      childColumns={childColumns()}
      fetchChildRows={fetchChildRows}
      fetchMoreChildRows={fetchMoreChildRows}
      data={transformedSeriesData || []}
      showLoader={
        (aggregatedEventsLoading && !aggregatedEventsError) ||
        (isLoading && !error)
      }
      loader={LoaderSkeleton}
      loaderCount={20}
      childLoader={EventLoaderSkeleton}
      onSort={handleSort}
      onExpandCollapse={handleExpandCollapse}
      sortConfig={{
        name: sortConfig.name as keyof AggregatedSeriesType,
        direction: sortConfig.reverseSort ? 'asc' : 'desc',
      }}
      noResults={() => (filters.searchText ? NoFilterResults : NoResults)}
      error={props => <ErrorScreen {...props} showButton={true} />}
      isError={!!aggregatedEventsError || !!error}
      hierarchyId={'id'}
      fetchMore={fetchMore}
      getChildLoaderCount={getChildLoaderCount}
      childGridPageSize={CHILD_GRID_PAGE_SIZE}
      disableTriStateSort={true}
    />
  );
};
